import { motion } from "framer-motion";
import { Wind, Snowflake, BatteryFull, Gauge } from "lucide-react";

const CHAPTERS = [
    {
        n: "01",
        icon: Wind,
        title: "The Wind",
        stat: "9 m/s",
        copy: "Most mini fans whisper. This one moves air like it means it — a focused stream that actually cools you down on the hottest afternoons.",
        span: "md:col-span-8",
    },
    {
        n: "02",
        icon: Snowflake,
        title: "The Ice",
        stat: "3 seconds",
        copy: "The IcePlate edition adds a chilling cold plate. Touch it to your wrist or neck and feel the temperature drop almost instantly.",
        span: "md:col-span-4",
    },
    {
        n: "03",
        icon: BatteryFull,
        title: "The Endurance",
        stat: "12 hours",
        copy: "A 3600mAh cell keeps the breeze going from your morning commute to your late-night study session. Recharge over Type-C.",
        span: "md:col-span-4",
    },
    {
        n: "04",
        icon: Gauge,
        title: "The Control",
        stat: "1–100 speeds",
        copy: "A precise digital dial, not three vague buttons. Dial a gentle 12 for the library or a full-blast 100 for the festival queue — the display shows exactly where you are.",
        span: "md:col-span-8",
    },
];

export default function Manifesto() {
    return (
        <section id="why" data-testid="manifesto-section" className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-32">
            <motion.p
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="text-xs uppercase tracking-[0.3em] text-cyan-300 mb-3"
            >
                Why this fan
            </motion.p>
            <motion.h2
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="font-display uppercase font-700 tracking-tighter text-4xl md:text-6xl mb-14"
            >
                Small body. <span className="text-cyan-300">Serious cold.</span>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {CHAPTERS.map((c, idx) => (
                    <motion.div
                        key={c.n}
                        initial={{ opacity: 0, y: 48 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.9, delay: (idx % 2) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                        data-testid={`manifesto-chapter-${c.n}`}
                        className={`relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-white/[0.03] p-8 md:p-10 group hover:border-cyan-300/40 transition-colors duration-500 ${c.span}`}
                    >
                        <span aria-hidden className="font-display absolute -top-6 -right-2 text-[9rem] md:text-[11rem] leading-none font-700 text-white/5 select-none group-hover:text-cyan-300/10 transition-colors duration-700">
                            {c.n}
                        </span>
                        <c.icon className="w-6 h-6 text-cyan-300 mb-6" />
                        <p className="font-display text-4xl md:text-5xl uppercase tracking-tight text-white">{c.stat}</p>
                        <h3 className="font-display uppercase tracking-[0.15em] text-sm text-cyan-200/90 mt-2 mb-4">{c.title}</h3>
                        <p className="text-sm md:text-base text-zinc-400 leading-relaxed max-w-lg relative z-10">{c.copy}</p>
                    </motion.div>
                ))}
            </div>
        </section>
    );
}
